import { useCallback } from 'react';
import { useShareSheetStore, type OpenShareSheetOptions } from '@stores/shareSheetStore';
import { getWebOrigin } from '@lib/platform';
import { spaceInviteTokenForUrl } from '@lib/spaces/inviteLink';
import {
  createQuestionShareContext,
  createEventShareContext,
  createProfileShareContext,
  createCommunityShareContext,
  ShareScenario,
  type ShareContext,
} from '@utils/shareHelpers';
import {
  getQuestionPath,
  getEventPath,
  getProfilePath,
  getCommunityPath,
} from '@utils/slugHelpers';
import type { QuestionClaimStatus } from '@models/api';

/**
 * Hook for controlling the global share sheet from any component.
 *
 * Provides both low-level access to open/close the sheet with any ShareContext,
 * and convenience methods for common share scenarios (questions, events, profiles).
 *
 * Note: Platform-specific short URL pre-fetching is now handled by the
 * SocialShareSheet component using usePrefetchShareUrls. This hook no longer
 * pre-fetches URLs, but still builds the share context with the path for
 * on-demand URL generation.
 *
 * @example
 * // Basic usage with custom context
 * const { openShareSheet, closeShareSheet } = useShareSheet();
 * openShareSheet(myContext, { delay: 300 });
 *
 * @example
 * // Convenience method for sharing a question after creation
 * const { openQuestionShareSheet } = useShareSheet();
 * openQuestionShareSheet({
 *   question: createdQuestion,
 *   sharerId: user.id,
 *   scenario: ShareScenario.JustPosted,
 * }, { delay: 300 });
 */
export const useShareSheet = () => {
  const storeOpenShareSheet = useShareSheetStore(state => state.openShareSheet);
  const storeCloseShareSheet = useShareSheetStore(state => state.closeShareSheet);

  /**
   * Open the share sheet with a custom context
   */
  const openShareSheet = useCallback(
    (context: ShareContext, options?: OpenShareSheetOptions) => {
      storeOpenShareSheet(context, options);
    },
    [storeOpenShareSheet],
  );

  /**
   * Close the share sheet
   */
  const closeShareSheet = useCallback(() => {
    storeCloseShareSheet();
  }, [storeCloseShareSheet]);

  /**
   * Open share sheet for a question
   *
   * @param params.question - The question object with id, slug, title, event_summaries, etc.
   * @param params.sharerId - The ID of the user sharing
   * @param params.scenario - The share scenario (JustPosted, OwnContent, OthersContent)
   * @param params.isAnonymous - Whether the question is anonymous
   * @param params.visibility - The question visibility setting
   * @param options - Optional delay configuration
   */
  const openQuestionShareSheet = useCallback(
    (
      params: {
        question: {
          id: string;
          slug?: string | null;
          title: string;
          event_summaries?: { id: string | null; name?: string | null; slug?: string | null }[];
          is_anonymous?: boolean;
          asked_for_self?: boolean;
          asked_for_user?: { full_name?: string | null } | null;
          asked_for_email?: string | null;
          visibility?: string;
          claim_status?: QuestionClaimStatus | null;
        };
        sharerId: string;
        scenario: ShareScenario;
        isOwn?: boolean;
      },
      options?: OpenShareSheetOptions,
    ) => {
      const { question, sharerId, scenario, isOwn = true } = params;

      const resolvedScenario =
        question.asked_for_self === false
          ? scenario === ShareScenario.JustPosted
            ? ShareScenario.JustPostedOnBehalf
            : scenario === ShareScenario.OwnContent
              ? ShareScenario.OwnContentOnBehalf
              : scenario === ShareScenario.OthersContent
                ? ShareScenario.OthersContentOnBehalf
                : scenario
          : scenario;

      const eventIds =
        question.event_summaries?.map(e => e.id).filter((id): id is string => id != null) ?? [];
      const eventNames = question.event_summaries?.map(e => e.name ?? '') ?? [];
      const primaryEventSlug = question.event_summaries?.[0]?.slug ?? null;
      const isMeetverse = eventIds.length === 0;

      // Build the path for on-demand platform-specific URL generation
      const path = getQuestionPath(question);

      // Note: Platform-specific short URLs are now pre-fetched by SocialShareSheet
      // when it opens, via usePrefetchShareUrls hook. The 'url' field here is used
      // for display/preview purposes only.
      const context = createQuestionShareContext({
        title: question.title,
        url: `${getWebOrigin()}${path}`, // Display URL (actual short URLs fetched by component)
        path,
        questionId: question.id,
        questionSlug: question.slug,
        sharerId,
        isOwn,
        scenario: resolvedScenario,
        eventIds,
        eventNames,
        primaryEventSlug,
        isMeetverse,
        isAnonymous: question.is_anonymous,
        askedForSelf: question.asked_for_self,
        askedForName: question.asked_for_user?.full_name ?? undefined,
        askedForEmail: question.asked_for_email ?? undefined,
        visibility: question.visibility,
        isClaimed: question.claim_status === 'CLAIMED',
      });

      storeOpenShareSheet(context, options);
    },
    [storeOpenShareSheet],
  );

  /**
   * Open share sheet for an event
   *
   * @param params.event - The event object with id, slug, name
   * @param params.sharerId - The ID of the user sharing
   * @param params.scenario - The share scenario
   * @param params.isAttending - Whether the user is attending the event
   * @param options - Optional delay configuration
   */
  const openEventShareSheet = useCallback(
    (
      params: {
        event: {
          id: string;
          slug?: string | null;
          name: string;
        };
        sharerId: string;
        scenario: ShareScenario;
        isOwn?: boolean;
        isAttending?: boolean;
        imageUrl?: string;
      },
      options?: OpenShareSheetOptions,
    ) => {
      const { event, sharerId, scenario, isOwn = false, isAttending, imageUrl } = params;

      // Share always means "share the real thing" (design §9): creator and peer share/copy both
      // target the event page. The share sheet's QR action derives its `?source=qr_event` link
      // from this same `path` (see SocialShareSheet's qrTarget), so it needs nothing from this
      // context beyond what createEventShareContext already carries, and no link the app
      // generates points at /welcome.
      const path = getEventPath(event);

      // Note: Platform-specific short URLs are now pre-fetched by SocialShareSheet
      // when it opens, via usePrefetchShareUrls hook.
      const context = createEventShareContext({
        title: event.name,
        url: `${getWebOrigin()}${path}`, // Display URL (actual short URLs fetched by component)
        path,
        eventId: event.id,
        eventSlug: event.slug,
        sharerId,
        isOwn,
        scenario,
        isAttending,
        imageUrl,
      });

      storeOpenShareSheet(context, options);
    },
    [storeOpenShareSheet],
  );

  /**
   * Open share sheet for a profile
   *
   * @param params.profile - The profile object with id, slug, full_name
   * @param params.sharerId - The ID of the user sharing
   * @param params.scenario - The share scenario
   * @param params.connectionStatus - The connection status with this profile
   * @param options - Optional delay configuration
   */
  const openProfileShareSheet = useCallback(
    (
      params: {
        profile: {
          id: string;
          slug?: string | null;
          full_name: string;
        };
        sharerId: string;
        scenario: ShareScenario;
        isOwn?: boolean;
        connectionStatus?: string;
      },
      options?: OpenShareSheetOptions,
    ) => {
      const { profile, sharerId, scenario, isOwn = false, connectionStatus } = params;

      // Build the path for on-demand platform-specific URL generation
      const path = getProfilePath(profile);

      // Note: Platform-specific short URLs are now pre-fetched by SocialShareSheet
      // when it opens, via usePrefetchShareUrls hook.
      const context = createProfileShareContext({
        title: profile.full_name,
        url: `${getWebOrigin()}${path}`, // Display URL (actual short URLs fetched by component)
        path,
        profileId: profile.id,
        profileSlug: profile.slug,
        sharerId,
        isOwn,
        scenario,
        connectionStatus,
      });

      storeOpenShareSheet(context, options);
    },
    [storeOpenShareSheet],
  );

  /**
   * Open share sheet for a community (invite flow)
   *
   * @param params.community - The community object with id, slug, name, image_url
   * @param params.sharerId - The ID of the user sharing
   * @param params.scenario - The share scenario
   * @param params.isMember - Whether the user is a member
   * @param params.isAdmin - Whether the user is an admin
   * @param options - Optional delay configuration
   */
  const openCommunityShareSheet = useCallback(
    (
      params: {
        community: {
          id: string;
          slug?: string | null;
          name: string;
          image_url?: string | null;
          is_unlisted?: boolean;
          invite_token?: string | null;
        };
        sharerId: string;
        scenario: ShareScenario;
        isOwn?: boolean;
        isMember?: boolean;
        isAdmin?: boolean;
      },
      options?: OpenShareSheetOptions,
    ) => {
      const { community, sharerId, scenario, isOwn = false, isMember, isAdmin } = params;

      // Peer shares open the community itself — as do organizer links now that
      // buildSpaceInviteLink no longer targets /welcome (D17 retired; see
      // docs/onboarding/onboarding-design.md §2, §9). Onboarding is
      // delivered by the entry redirect on landing, so both link kinds are the
      // same shape and differ only in their `source` attribution. Unlisted
      // communities bake the invite token into the path so all downstream URL
      // generation (short URLs, platform shares) preserves it.
      const canonicalPath = getCommunityPath(community);
      const inviteToken = spaceInviteTokenForUrl(community);
      // `shareUrl` is derived from `path`, never the reverse: `path` travels into
      // the context separately, so rebuilding it from a URL's `pathname` would
      // strip the token from every platform share while the display URL still
      // looked correct.
      // Encoded, matching buildSpaceInviteLink: both build the same path shape for the same
      // Space, and an unencoded token here would silently diverge the two the day the token
      // alphabet widens past [A-Za-z0-9_-].
      const path = inviteToken
        ? `${canonicalPath}?invite=${encodeURIComponent(inviteToken)}`
        : canonicalPath;
      const shareUrl = `${getWebOrigin()}${path}`;

      const context = createCommunityShareContext({
        title: community.name,
        url: shareUrl,
        path,
        communityId: community.id,
        communitySlug: community.slug,
        sharerId,
        isOwn,
        scenario,
        isMember,
        isAdmin,
        imageUrl: community.image_url ?? undefined,
      });

      storeOpenShareSheet(context, options);
    },
    [storeOpenShareSheet],
  );

  return {
    openShareSheet,
    closeShareSheet,
    openQuestionShareSheet,
    openEventShareSheet,
    openProfileShareSheet,
    openCommunityShareSheet,
  };
};
